
import { useState } from 'react';
import { Copy, Eye, EyeOff, Trash2, Calendar } from 'lucide-react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatLocalDate } from '@/lib/date';

export interface ApiKey {
  id: string;
  name: string;
  token: string;
  createdAt: string;
  status: 'active' | 'revoked' | 'expired';
  scopes: string[];
  environment?: string;
  expiresAt?: string | null;
  role?: string;
  ttlDays?: number;
}

interface ApiKeysListProps {
  keys: ApiKey[];
  onRevoke: (id: string) => void;
}

const ApiKeysList = ({ keys, onRevoke }: ApiKeysListProps) => {
  const { toast } = useToast();
  const [revokeConfirm, setRevokeConfirm] = useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});

  const toggleKeyVisibility = (id: string) => {
    setRevealedKeys(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "API key has been copied to your clipboard.",
    });
  };

  const handleRevoke = (id: string) => {
    onRevoke(id);
    setRevokeConfirm(null);
  };

  const formatToken = (token: string, revealed: boolean) => {
    if (revealed) {
      return token;
    }
    return `${token.substring(0, 8)}****************`;
  };

  // Function to check if a key is expiring soon (within 7 days)
  const isExpiringSoon = (expiresAt: string | null | undefined): boolean => {
    if (!expiresAt) return false;
    
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(now.getDate() + 7);
    
    return expiryDate <= sevenDaysFromNow && expiryDate > now;
  };

  // Function to format expiry status
  const getExpiryStatus = (key: ApiKey) => {
    if (!key.expiresAt) return null;
    
    if (isExpiringSoon(key.expiresAt)) {
      return (
        <Badge variant="outline" className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
          <Calendar size={12} className="mr-1" />
          Expires soon
        </Badge>
      );
    }
    
    // If it's already expired but status hasn't been updated
    const isExpired = new Date(key.expiresAt) < new Date();
    if (isExpired) {
      return (
        <Badge variant="outline" className="bg-red-500/20 text-red-500 border-red-500/30">
          Expired
        </Badge>
      );
    }
    
    return (
      <span className="text-xs text-muted-foreground">
        Expires: {formatLocalDate(key.expiresAt)}
      </span>
    );
  };

  return (
    <>
      <div className="table-container overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-[25%]">Token Prefix</TableHead>
              <TableHead>Environment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>TTL</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No API keys match your filters
                </TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      {key.name}
                      {key.expiresAt && getExpiryStatus(key)}
                    </div>
                  </TableCell>
                  <TableCell className="font-geist-mono text-xs">
                    <div className="flex items-center gap-2">
                      <code>{formatToken(key.token, !!revealedKeys[key.id])}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => toggleKeyVisibility(key.id)}
                      >
                        {revealedKeys[key.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(key.token)}
                      >
                        <Copy size={14} />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    {key.environment && (
                      <Badge 
                        variant="outline" 
                        className={`
                          ${key.environment === 'production' ? 'bg-recallio-green/20 text-recallio-green border-recallio-green/30' : ''}
                          ${key.environment === 'staging' ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' : ''}
                          ${key.environment === 'development' ? 'bg-blue-500/20 text-blue-500 border-blue-500/30' : ''}
                        `}
                      >
                        {key.environment.charAt(0).toUpperCase() + key.environment.slice(1)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={key.status === 'active' ? 'default' : 'destructive'}
                      className={`
                        ${key.status === 'active' ? 'bg-recallio-green text-recallio-background' : ''}
                        ${key.status === 'expired' ? 'bg-yellow-500' : ''}
                      `}
                    >
                      {key.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge variant="outline" className="bg-recallio-medium-gray hover:bg-recallio-light-gray/50 cursor-help">
                            {key.role}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">
                            {key.role === 'owner' ? 'Full access to all resources' : 
                             key.role === 'admin' ? 'Admin access with some restrictions' : 
                             'Standard user access'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    {key.ttlDays ? (
                      <span className="text-sm text-muted-foreground">
                        {key.ttlDays} days
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        No limit
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeConfirm(key.id)}
                        disabled={key.status !== 'active'}
                        className={key.status === 'active' ? 'text-destructive hover:text-destructive hover:bg-destructive/10' : ''}
                      >
                        <Trash2 size={16} className="mr-2" />
                        Revoke
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={!!revokeConfirm} onOpenChange={() => setRevokeConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Key Revocation</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            Are you sure you want to revoke this API key? This action cannot be undone, and any services using this key will immediately lose access.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => revokeConfirm && handleRevoke(revokeConfirm)}
            >
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApiKeysList;
